import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { MenuItem } from '../features/menu/types';

type HeaderNavigationOptions = {
  search?: Record<string, unknown>;
  state?: unknown;
  preferCachedOnMount?: boolean;
};

type PageHeaderState = {
  title: string;
  navButtons?: MenuItem[];
  onNavigate?: (path: string, options?: HeaderNavigationOptions) => void;
  headerActions?: ReactNode;
};

type PageHeaderContextValue = PageHeaderState & {
  setPageHeader: (state: PageHeaderState) => void;
  clearPageHeader: () => void;
};

const PageHeaderContext = createContext<PageHeaderContextValue>({
  title: '',
  setPageHeader: () => {},
  clearPageHeader: () => {},
});

export const PageHeaderProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<PageHeaderState>({ title: '' });

  const setPageHeader = useCallback((next: PageHeaderState) => {
    setState(next);
  }, []);

  const clearPageHeader = useCallback(() => {
    setState({ title: '' });
  }, []);

  return (
    <PageHeaderContext.Provider value={{ ...state, setPageHeader, clearPageHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

// Context files conventionally export both Provider and hook from the same file.
// eslint-disable-next-line react-refresh/only-export-components
export const usePageHeader = () => useContext(PageHeaderContext);
